'use client';

import { FC, useState } from 'react';
import { Dialog, DialogPanel } from '@headlessui/react';
import { useUiStore } from '@/data';
import {
  Accordion,
  Close,
  Heading,
  Icon,
  Link,
  PrismicImageWithAlt,
  useData,
} from '@/components';
import { sortBy } from 'lodash';
import { PAGE_ROUTES } from '@/constants';
import classNames from 'classnames';
import * as prismicH from '@prismicio/helpers';
import {
  NavigationDocumentDataLinksItem,
  SideNavigationDocumentDataFooterLinksItem,
  SideNavigationDocumentDataLinksItem,
  Simplify,
} from 'prismicio-types';
import { GroupField } from '@prismicio/client';

import { SideNavLink } from './SideNavLink';

type SideNavLinkItem =
  | NavigationDocumentDataLinksItem
  | SideNavigationDocumentDataLinksItem;

const SideNavigation: FC = () => {
  const [state, setState] = useUiStore();
  const [isOpen, setIsOpen] = useState({});
  const { header, navigation, sideNavigation } = useData();
  const hasSideNavigationDoc = !!sideNavigation?.data;
  const openExternalInNewTab =
    sideNavigation?.data?.open_external_links_in_new_tab ?? false;
  const closeNav = () => setState({ is_nav_open: false });
  const headerLogo = prismicH.isFilled.image(header?.data?.mobile_logo)
    ? header?.data?.mobile_logo
    : header?.data?.logo;
  const sideNavLogo = prismicH.isFilled.image(sideNavigation?.data?.logo)
    ? sideNavigation?.data?.logo
    : undefined;
  const logo = sideNavLogo ?? headerLogo;
  let sideNavItem: SideNavLinkItem[] = navigation?.data?.links ?? [];
  let sideNavFooterItem: GroupField<
    Simplify<SideNavigationDocumentDataFooterLinksItem>
  > = [];

  if (hasSideNavigationDoc) {
    sideNavItem = sideNavigation?.data?.links ?? [];
    sideNavFooterItem = sideNavigation?.data?.footer_links || [];
  }

  const makeElement = (item, index) => {
    if (item?.link) {
      return (
        <SideNavLink
          className={'side-nav__item'}
          internalLink={item.link}
          key={index}
          label={item.label}
          onNavigate={closeNav}
          openExternalInNewTab={openExternalInNewTab}
        />
      );
    }

    const externalLink = item?.external_link;
    const isMegaMenu =
      prismicH.isFilled.link(externalLink) &&
      externalLink.link_type === 'Document' &&
      ['mega_nav', 'custom_mega_menu'].includes(externalLink.type);

    if (prismicH.isFilled.link(externalLink) && !isMegaMenu) {
      return (
        <SideNavLink
          className={'side-nav__item'}
          externalLink={externalLink}
          key={index}
          label={item.label}
          onNavigate={closeNav}
          openExternalInNewTab={openExternalInNewTab}
          preferExternal
        />
      );
    }

    const uid = externalLink?.uid;

    let megaMenuNavLinks =
      externalLink?.data?.body?.filter(
        filter => filter.slice_type === 'group_link',
      ) || [];

    if (externalLink?.type === 'custom_mega_menu') {
      megaMenuNavLinks =
        externalLink?.data?.slices?.filter(
          filter => filter.slice_type === 'group_link',
        ) || [];
    }

    const megaMenuNavLinksSorted = sortBy(
      megaMenuNavLinks,
      o => o?.primary?.sort,
    );

    return (
      <Accordion
        isOpen={isOpen[uid]}
        key={index}
        title={
          <div className={'side-nav__item'}>
            {item?.label}
            <Icon
              aria-hidden="true"
              className={'icon'}
              name={isOpen[uid] ? 'angle-up' : 'angle-down'}
            />
          </div>
        }
        toggle={() =>
          setIsOpen(oldIsOpen => ({
            ...oldIsOpen,
            [uid]: !oldIsOpen[uid],
          }))
        }
      >
        {isOpen[uid] &&
          megaMenuNavLinksSorted.map((item, index) => {
            return (
              <div key={index}>
                {item?.primary?.group_link_label?.[0] && (
                  <Heading className={'inmediate_child'} level={3}>
                    {prismicH.asText(item?.primary?.group_link_label)}
                  </Heading>
                )}

                {item.items.map((sChild, index) => {
                  return (
                    <Link
                      aria-label={`Go to ${sChild?.link}`}
                      className={'secondary_child'}
                      href={sChild?.link}
                      key={index}
                      onClick={() => {
                        setState({
                          is_nav_open: false,
                        });
                      }}
                    >
                      {sChild?.label}
                    </Link>
                  );
                })}
              </div>
            );
          })}
      </Accordion>
    );
  };

  return (
    <Dialog
      aria-label="Mobile menu"
      aria-modal="true"
      className={classNames('side-nav', {
        'side-nav_open': state.is_nav_open,
      })}
      onClose={closeNav}
      open={state.is_nav_open}
      role="dialog"
      static
      tabIndex={-1}
    >
      <DialogPanel className={'side-nav__container'}>
        <div className={'side-nav__header'}>
          <Link aria-label={'Go to home'} className="logo" href={'/'}>
            <figure>
              <PrismicImageWithAlt
                defaultAlt="Brand logo"
                field={logo}
                height={logo?.dimensions?.height}
                quality={100}
                style={{
                  objectFit: 'contain',
                  width: '100%',
                  height: 'auto',
                }}
                width={logo?.dimensions?.width}
              />
            </figure>
          </Link>
          <Close onClick={closeNav} />
        </div>
        <nav aria-label="Mobile menu links" className={'side-nav__body'}>
          <div className={'side-nav__main'}>
            {!hasSideNavigationDoc && (
              <p
                className={classNames(
                  'heading',
                  'heading_level-4',
                  'side-nav__main-heading',
                )}
              >
                Categories
              </p>
            )}
            {sideNavItem.map((item, index) => {
              return makeElement(item, index);
            })}
          </div>
          {!hasSideNavigationDoc && (
            <Link
              aria-label={'Go to all location page'}
              className={'button_location'}
              href={PAGE_ROUTES.STORE_LOCATOR}
              onClick={closeNav}
            >
              <Icon name="pin_drop" />
              Store Location
            </Link>
          )}
          {hasSideNavigationDoc &&
            sideNavFooterItem?.map(
              (item: SideNavigationDocumentDataFooterLinksItem, index) => {
                const textTransform = item?.uppercase_label
                  ? 'uppercase'
                  : 'capitalize';

                return (
                  <SideNavLink
                    className={'button_location'}
                    externalLink={item.external_link}
                    internalLink={item.link}
                    key={index}
                    label={item.label ?? ''}
                    onNavigate={closeNav}
                    openExternalInNewTab={openExternalInNewTab}
                    preferExternal
                    style={{ textTransform }}
                  >
                    {item?.image?.url && (
                      <figure
                        aria-hidden={item?.label ? true : undefined}
                        className={'button_location_image'}
                      >
                        <PrismicImageWithAlt
                          defaultAlt={item?.label ? '' : 'Icon'}
                          field={item?.image}
                          fill
                          style={{
                            objectFit: 'contain',
                          }}
                        />
                      </figure>
                    )}
                    {item?.label}
                  </SideNavLink>
                );
              },
            )}
        </nav>
      </DialogPanel>
      <div aria-hidden="true" className={'side-nav__overlap'} />
    </Dialog>
  );
};

export default SideNavigation;
